import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, Upload, Settings, LogOut, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/app", end: true, label: "Tableau de bord", icon: LayoutDashboard, testid: "side-nav-dashboard" },
  { to: "/app/factures", label: "Factures", icon: FileText, testid: "side-nav-invoices" },
  { to: "/app/importer", label: "Importer", icon: Upload, testid: "side-nav-import" },
  { to: "/app/abonnement", label: "Abonnement", icon: CreditCard, testid: "side-nav-subscription" },
  { to: "/app/parametres", label: "Paramètres", icon: Settings, testid: "side-nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const onLogout = async () => {
    await logout();
    toast.success("Déconnexion réussie");
    navigate("/", { replace: true });
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-4 py-3 text-sm transition-colors duration-200 ${
      isActive ? "bg-[#2563EB]/15 text-white border border-[#2563EB]/30" : "text-zinc-400 hover:text-white hover:bg-white/5 border border-transparent"
    }`;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="app-shell">
      {/* Sidebar desktop */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r border-white/10 bg-[#0A0E17] z-40" data-testid="app-sidebar">
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <span className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#00D4FF] flex items-center justify-center font-display text-[13px] font-extrabold text-[#050505]">FX</span>
          <span className="ml-2.5 font-display text-sm tracking-tight">Factur<span className="text-[#00D4FF]">Xpress</span></span>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1">
          {NAV.map(({ to, end, label, icon: Icon, testid }) => (
            <NavLink key={to} to={to} end={end} data-testid={testid} className={linkCls}>
              <Icon className="h-4 w-4" />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-2" data-testid="user-chip">
            <span className="h-9 w-9 rounded-full bg-[#2563EB]/20 border border-[#2563EB]/40 flex items-center justify-center font-display text-xs text-[#00D4FF]">
              {(user?.name || user?.email || "U").slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.name || "Utilisateur"}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
            <button onClick={onLogout} data-testid="logout-button" title="Déconnexion" className="p-2 rounded-lg text-zinc-500 hover:text-[#FF3B30] hover:bg-white/5 transition-colors duration-200">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Topbar mobile */}
      <header className="md:hidden sticky top-0 z-40 glass-panel border-x-0 border-t-0 h-14 flex items-center justify-between px-4" data-testid="app-topbar">
        <div className="flex items-center gap-2">
          <span className="h-7 w-7 rounded-lg bg-gradient-to-br from-[#2563EB] to-[#00D4FF] flex items-center justify-center font-display text-[11px] font-extrabold text-[#050505]">FX</span>
          <span className="font-display text-sm">Factur<span className="text-[#00D4FF]">Xpress</span></span>
        </div>
        <button onClick={onLogout} data-testid="logout-button-mobile" title="Déconnexion" className="p-2 rounded-lg text-zinc-400 hover:text-[#FF3B30] transition-colors duration-200">
          <LogOut className="h-4 w-4" />
        </button>
      </header>

      <main className="md:ml-64 px-4 md:px-10 py-6 md:py-10 pb-24 md:pb-10">
        <Outlet />
      </main>

      {/* Bottom nav mobile */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass-panel border-x-0 border-b-0 grid grid-cols-5" data-testid="app-bottomnav">
        {NAV.map(({ to, end, label, icon: Icon, testid }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={`${testid}-mobile`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium transition-colors duration-200 ${isActive ? "text-[#00D4FF]" : "text-zinc-500"}`
            }
          >
            <Icon className="h-5 w-5" />
            {label.split(" ")[0]}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
